import { Link } from "react-router-dom";
import {
  ArrowRight,
  CalendarClock,
  ClipboardList,
  FileDown,
  GraduationCap,
  TrendingUp,
  Wallet,
  type LucideIcon,
} from "lucide-react";
import {
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
  PageHeader,
  PageHeaderDescription,
  PageHeaderHeading,
  PageHeaderTitle,
  PageHeaderTop,
} from "@eduviaos/ui-kit";
import { useEnabledModules } from "@/hooks/useEnabledModules";
import { useTerms } from "@/hooks/useTerms";
import { getVerticalProfile } from "@/lib/verticalProfile";

/**
 * Reports hub (Phase SC-22e). Every report already lives on the page that
 * owns its data — profit and loss on Accounting, collections on Fees, and so
 * on — so this page only points to them instead of repeating the numbers.
 */

interface ReportLink {
  key: string;
  title: string;
  description: string;
  to: string;
  icon: LucideIcon;
  /** Shown when any of these modules is on. */
  modules: readonly string[];
  learnerPortalOnly?: boolean;
}

const REPORT_LINKS: readonly ReportLink[] = [
  {
    key: "pnl",
    title: "Profit & loss",
    description: "Fee income against staff cost and expenses, month by month.",
    to: "/accounting",
    icon: TrendingUp,
    modules: ["accounting"],
  },
  {
    key: "fees",
    title: "Fee collections",
    description: "Plans, invoices, and what is still due.",
    to: "/fees",
    icon: Wallet,
    modules: ["fees"],
  },
  {
    key: "admissions",
    title: "Enquiries and admissions",
    description: "Where each student is in your funnel, with CSV exports.",
    to: "/admissions",
    icon: ClipboardList,
    modules: ["admissions", "crm"],
  },
  {
    key: "attendance",
    title: "Attendance",
    description: "Daily attendance and monthly patterns.",
    to: "/attendance",
    icon: CalendarClock,
    modules: ["attendance"],
  },
  {
    key: "learning",
    title: "Learner progress",
    description: "Plans, reviews due, certificates, and reading logs.",
    to: "/learning",
    icon: GraduationCap,
    modules: ["learning"],
    learnerPortalOnly: true,
  },
  {
    key: "audit",
    title: "Audit export",
    description: "Download who changed what, for your records.",
    to: "/audit-export",
    icon: FileDown,
    modules: ["audit_export"],
  },
];

export function ReportsPage() {
  const { vertical, isModuleEnabled } = useEnabledModules();
  const { ct } = useTerms(vertical);
  const hasLearnerPortal = vertical ? getVerticalProfile(vertical).has.learnerPortal : false;
  const links = REPORT_LINKS.filter(
    (link) =>
      link.modules.some((key) => isModuleEnabled(key)) &&
      (!link.learnerPortalOnly || hasLearnerPortal),
  );

  return (
    <div className="mx-auto max-w-6xl py-8">
      <PageHeader>
        <PageHeaderTop>
          <PageHeaderHeading>
            <PageHeaderTitle>Reports</PageHeaderTitle>
            <PageHeaderDescription>
              Each report lives with the work it measures. Pick one to open it.
            </PageHeaderDescription>
          </PageHeaderHeading>
        </PageHeaderTop>
      </PageHeader>

      <ul className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" aria-label="Reports">
        {links.map((link) => {
          const Icon = link.icon;
          return (
            <li key={link.key}>
              <Link
                to={link.to}
                className="focus-visible:ring-ring group block h-full rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
                data-testid={`report-link-${link.key}`}
              >
                <Card className="border-border/80 group-hover:border-primary/50 h-full transition-colors duration-150 ease-out">
                  <CardHeader>
                    <div className="flex items-start gap-3">
                      <span
                        aria-hidden="true"
                        className="bg-primary/10 text-primary grid size-10 shrink-0 place-items-center rounded-lg"
                      >
                        <Icon className="size-5" />
                      </span>
                      <div className="min-w-0 flex-1">
                        <CardTitle className="flex items-center gap-1 text-base font-semibold">
                          {ct(link.title)}
                          <ArrowRight
                            aria-hidden="true"
                            className="text-muted-foreground size-4 transition-transform duration-150 ease-out motion-safe:group-hover:translate-x-0.5"
                          />
                        </CardTitle>
                        <CardDescription className="mt-1">{ct(link.description)}</CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                </Card>
              </Link>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
